/**
 * PAIQ Dialog - Prompt AI Quality optimization workflow
 *
 * 2-step wizard (the Optimize button runs generatePrePrompt before opening):
 *   Step 1: Review & edit optimized prompt → Boost & Score
 *            (generateFinalPrompt / METAPROMPT2 → generateSmartPrompt → calculateIQ)
 *   Step 2: View results (final prompt, score, dimensions, strengths/weaknesses) + actions
 */
import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PsychologyIcon from '@mui/icons-material/Psychology';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { paiqApi } from '../services/api';
import {
  useDraftAutosave,
  loadDraft,
  clearDraft,
  formatDraftAge,
  type StoredDraft,
} from '../hooks/useLocalDraft';

/* ------------------------------------------------------------------ */
/*  Types matching the backend response shapes                        */
/* ------------------------------------------------------------------ */

interface PAIQDimensionScore {
  score: number; // 1-5
  reasoning: string;
}

interface PAIQDimensions {
  firstPrinciplesDepth?: PAIQDimensionScore;
  cognitiveConstraints?: PAIQDimensionScore;
  crossDomainSynthesis?: PAIQDimensionScore;
  specificity?: PAIQDimensionScore;
  signalToNoise?: PAIQDimensionScore;
  actionability?: PAIQDimensionScore;
  critiqueLoop?: PAIQDimensionScore;
}

export interface CalculatePromptIQResponse {
  paiqScore: number;
  maxScore: number;
  paiqGrade: string;
  dimensions?: PAIQDimensions;
  strengths?: string[];
  weaknesses?: string[];
  improvements?: string[];
  rewrittenPrompt?: string;
}

/* ------------------------------------------------------------------ */
/*  Props                                                             */
/* ------------------------------------------------------------------ */

interface PAIQDialogProps {
  open: boolean;
  onClose: () => void;
  initialPrompt?: string;
  /** Called when the user saves the optimized prompt (with full PAIQ analysis if available) */
  onSave: (optimizedPrompt: string, paiqScore?: number, paiqAnalysis?: CalculatePromptIQResponse) => void;
  /** Optional system prompt for LLM */
  systemPrompt?: string;
  /** Optional output format to append to prompt */
  outputFormat?: string;
  /** Optional persona role context for the optimization */
  personaRoleContext?: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

/** localStorage key for the in-progress PAIQ dialog draft (one dialog open at a time). */
const PAIQ_DRAFT_KEY = 'paiq-dialog';

interface PaiqDraft {
  editablePrompt: string;
  finalPrompt: string;
  paiqResult: CalculatePromptIQResponse | null;
  activeStep: number;
}

function getGradeColor(grade: string): 'success' | 'info' | 'warning' | 'error' | 'default' {
  switch (grade.toLowerCase()) {
    case 'exceptional':
    case 'excellent':
      return 'success';
    case 'good':
      return 'info';
    case 'adequate':
      return 'warning';
    default:
      return 'error';
  }
}

function getScoreBarColor(score: number): string {
  const pct = (score / 5) * 100;
  if (pct >= 80) return '#10b981';
  if (pct >= 60) return '#3b82f6';
  if (pct >= 40) return '#eab308';
  if (pct >= 20) return '#f97316';
  return '#ef4444';
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export default function PAIQDialog({ open, onClose, initialPrompt = '', onSave, systemPrompt, outputFormat, personaRoleContext }: PAIQDialogProps) {
  // Step state
  const [activeStep, setActiveStep] = useState(0);

  // Step 1 — Review & edit the pre-optimized prompt
  const [editablePrompt, setEditablePrompt] = useState(initialPrompt);

  // Boost & Score state
  const [isBoosting, setIsBoosting] = useState(false);
  const [boostStatus, setBoostStatus] = useState('');
  const [finalPrompt, setFinalPrompt] = useState('');
  const [paiqResult, setPaiqResult] = useState<CalculatePromptIQResponse | null>(null);

  // UI state
  const [showDimensions, setShowDimensions] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Recoverable draft found in localStorage when the dialog opens.
  const [restorable, setRestorable] = useState<StoredDraft<PaiqDraft> | null>(null);

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setEditablePrompt(initialPrompt);
      setActiveStep(0);
      setIsBoosting(false);
      setBoostStatus('');
      setFinalPrompt('');
      setPaiqResult(null);
      setShowDimensions(true);
      setError(null);
      setCopySuccess(false);

      // Offer to restore a prior unsaved draft (e.g. lost to a logout) when it
      // holds meaningfully different content than what we're opening with.
      const draft = loadDraft<PaiqDraft>(PAIQ_DRAFT_KEY);
      const draftText = draft?.data?.editablePrompt?.trim();
      if (draft && draftText && draftText !== initialPrompt.trim()) {
        setRestorable(draft);
      } else {
        setRestorable(null);
      }
    }
  }, [open, initialPrompt]);

  // Debounced autosave of in-progress work while the dialog is open.
  useDraftAutosave<PaiqDraft>(
    PAIQ_DRAFT_KEY,
    { editablePrompt, finalPrompt, paiqResult, activeStep },
    open && editablePrompt.trim().length > 0
  );

  /* ---------- Boost & Score handler ---------- */
  /* Chains: generatePrePrompt → generateFinalPrompt → calculateIQ */

  const handleBoostAndScore = async () => {
    if (!editablePrompt.trim()) {
      setError('Please provide a prompt to boost');
      return;
    }
    setError(null);
    setIsBoosting(true);
    try {
      // Step 2: generatePrePrompt - enhances with expert minds synthesispersonaRoleContext
      setBoostStatus('Adding expert minds synthesis...');
      const preRes = await paiqApi.generatePrePrompt(editablePrompt.trim(), personaRoleContext, systemPrompt, outputFormat);
      const prePrompt: string = preRes?.data?.prePrompt || preRes?.prePrompt || '';
      if (!prePrompt) {
        throw new Error('generatePrePrompt returned empty result');
      }

      // Step 3: generateFinalPrompt - refines and structures the prompt
      setBoostStatus('Refining and structuring prompt...');
      const finalRes = await paiqApi.generateFinalPrompt(prePrompt);
      const finalPromptText: string = finalRes?.data?.finalPrompt || finalRes?.finalPrompt || '';
      if (!finalPromptText) {
        throw new Error('generateFinalPrompt returned empty result');
      }
      setFinalPrompt(finalPromptText);

      // Step 4: calculateIQ — score the final enhanced prompt
      setBoostStatus('Calculating PAIQ score...');
      const paiqRes = await paiqApi.calculateIQ(finalPromptText);
      const paiqData = (paiqRes?.data ?? paiqRes) as CalculatePromptIQResponse;
      setPaiqResult(paiqData);
      setActiveStep(1);
    } catch (err) {
      console.error('Failed to boost and score prompt:', err);
      setError(err instanceof Error ? err.message : 'Failed to boost and score prompt');
    } finally {
      setIsBoosting(false);
      setBoostStatus('');
    }
  };

  /* ---------- actions ---------- */

  const handleCopy = async () => {
    const text = activeStep === 1 && finalPrompt ? finalPrompt : editablePrompt;
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const handleSave = () => {
    // On step 2, save the finalPrompt; otherwise fallback to editablePrompt
    const promptToSave = activeStep === 1 && finalPrompt ? finalPrompt : editablePrompt;
    onSave(promptToSave, paiqResult?.paiqScore, paiqResult ?? undefined);
    clearDraft(PAIQ_DRAFT_KEY);
    onClose();
  };

  const handleRestoreDraft = () => {
    if (!restorable) return;
    const d = restorable.data;
    setEditablePrompt(d.editablePrompt ?? '');
    setFinalPrompt(d.finalPrompt ?? '');
    setPaiqResult(d.paiqResult ?? null);
    setActiveStep(d.paiqResult ? d.activeStep ?? 0 : 0);
    setRestorable(null);
  };

  const handleDismissDraft = () => {
    setRestorable(null);
    clearDraft(PAIQ_DRAFT_KEY);
  };

  /* ---------- dimension renderer ---------- */

  const renderDimension = (label: string, dim: PAIQDimensionScore | undefined) => {
    if (!dim) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" fontWeight={500}>{label}</Typography>
          <Typography variant="body2" fontWeight={700}>{dim.score}/5</Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={(dim.score / 5) * 100}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': { backgroundColor: getScoreBarColor(dim.score), borderRadius: 4 },
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {dim.reasoning}
        </Typography>
      </Box>
    );
  };

  /* ---------- render ---------- */

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      scroll="paper"
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        // Only close via explicit X button, not backdrop click or escape
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #38bdf8 0%, #3b82f6 50%, #1e3a8a 100%)',
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ p: 0.75, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 1, display: 'flex' }}>
            <PsychologyIcon sx={{ color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} color="white">PAIQ — Prompt AI Intelligence Quotient</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </Box>

      {/* Stepper */}
      {/* <Box sx={{ px: 4, py: 1, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>
      </Box> */}

      {/* Content */}
      <DialogContent dividers sx={{ minHeight: 400 }}>
        {restorable && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <>
                <Button color="inherit" size="small" onClick={handleRestoreDraft}>
                  Restore
                </Button>
                <Button color="inherit" size="small" onClick={handleDismissDraft}>
                  Dismiss
                </Button>
              </>
            }
          >
            Found an unsaved draft from {formatDraftAge(restorable.savedAt)}. Restore it?
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ---- Step 1: Review optimized prompt & Boost ---- */}
        {activeStep === 0 && (
          <Stack spacing={2}>
            <Alert severity="success" variant="outlined" sx={{ mb: 1 }}>
              Your prompt has been optimized. Review and edit below, then click
              <b> Boost & Score</b> for further refinement and PAIQ scoring.
            </Alert>

            <Typography variant="subtitle2">Optimized Prompt (you can edit this)</Typography>
            <TextField
              fullWidth
              multiline
              rows={14}
              value={editablePrompt}
              onChange={(e) => setEditablePrompt(e.target.value)}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
            />

            {isBoosting && boostStatus && (
              <Alert severity="info" icon={<CircularProgress size={18} />}>
                {boostStatus}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={isBoosting ? <CircularProgress size={18} color="inherit" /> : <RocketLaunchIcon />}
                onClick={handleBoostAndScore}
                disabled={isBoosting || !editablePrompt.trim()}
                sx={{
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  '&:hover': { background: 'linear-gradient(135deg, #047857 0%, #059669 100%)' },
                }}
              >
                {isBoosting ? 'Boosting...' : 'Boost & Score'}
              </Button>
            </Box>
          </Stack>
        )}

        {/* ---- Step 2: PAIQ Results ---- */}
        {activeStep === 1 && paiqResult && (
          <Stack spacing={3}>
            {/* Final optimized prompt */}
            <Box>
              <Typography variant="subtitle2" color="info.dark" gutterBottom>
                ✨ Final Optimized Prompt
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={10}
                value={finalPrompt}
                onChange={(e) => setFinalPrompt(e.target.value)}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              />
            </Box>

            {/* Main score */}
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
                borderColor: 'primary.100',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body1" color="text.secondary" fontWeight={500}>PAIQ Score:</Typography>
                <Typography variant="h3" fontWeight={700} color="primary.main">
                  {paiqResult.paiqScore}
                  <Typography component="span" variant="h5" color="primary.light">/{paiqResult.maxScore || 35}</Typography>
                </Typography>
              </Box>
              <Chip
                label={paiqResult.paiqGrade}
                color={getGradeColor(paiqResult.paiqGrade)}
                sx={{ fontWeight: 600, fontSize: '0.9rem', px: 1 }}
              />
            </Paper>

            {/* Dimensions */}
            {paiqResult.dimensions && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box
                  onClick={() => setShowDimensions(!showDimensions)}
                  sx={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer', mb: showDimensions ? 2 : 0 }}
                >
                  <Typography variant="subtitle2">Score Breakdown by Dimension (1–5 each)</Typography>
                  <IconButton size="small">
                    {showDimensions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                <Collapse in={showDimensions}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 4 }}>
                    {renderDimension('First-Principles Depth', paiqResult.dimensions.firstPrinciplesDepth)}
                    {renderDimension('Cognitive Constraints', paiqResult.dimensions.cognitiveConstraints)}
                    {renderDimension('Cross-Domain Synthesis', paiqResult.dimensions.crossDomainSynthesis)}
                    {renderDimension('Specificity', paiqResult.dimensions.specificity)}
                    {renderDimension('Signal-to-Noise', paiqResult.dimensions.signalToNoise)}
                    {renderDimension('Actionability', paiqResult.dimensions.actionability)}
                    {renderDimension('Critique Loop', paiqResult.dimensions.critiqueLoop)}
                  </Box>
                </Collapse>
              </Paper>
            )}

            {/* Strengths & Weaknesses */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {paiqResult.strengths && paiqResult.strengths.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0fdf4', borderColor: '#bbf7d0' }}>
                  <Typography variant="subtitle2" color="success.dark" gutterBottom>✓ Strengths</Typography>
                  {paiqResult.strengths.map((s, i) => (
                    <Typography key={i} variant="body2" color="success.dark">• {s}</Typography>
                  ))}
                </Paper>
              )}
              {paiqResult.weaknesses && paiqResult.weaknesses.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fffbeb', borderColor: '#fde68a' }}>
                  <Typography variant="subtitle2" color="warning.dark" gutterBottom>⚠ Weaknesses</Typography>
                  {paiqResult.weaknesses.map((w, i) => (
                    <Typography key={i} variant="body2" color="warning.dark">• {w}</Typography>
                  ))}
                </Paper>
              )}
            </Box>

            {/* Improvements */}
            {paiqResult.improvements && paiqResult.improvements.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#eff6ff', borderColor: '#bfdbfe' }}>
                <Typography variant="subtitle2" color="primary.dark" gutterBottom>💡 Improvements</Typography>
                {paiqResult.improvements.map((imp, i) => (
                  <Typography key={i} variant="body2" color="primary.dark">• {imp}</Typography>
                ))}
              </Paper>
            )}

            <Divider />

            {/* Back navigation */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => { setActiveStep(0); setFinalPrompt(''); setPaiqResult(null); }} color="primary">
                Edit & Re-Score
              </Button>
            </Box>
          </Stack>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 1, bgcolor: 'background.paper' }}>
        <Button
          startIcon={copySuccess ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
          onClick={handleCopy}
        >
          {copySuccess ? 'Copied!' : 'Copy to Clipboard'}
        </Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} color="success">
          Save Prompt
        </Button>
      </DialogActions>
    </Dialog>
  );
}
